/* eslint-disable */
import React, { useMemo } from 'react';
import './ordersV3.css';
import { useOrdersUiStore } from '../../../portals/orders/ordersUiStore';
import { useDeskReady } from '../../../portals/orders/desk/useDeskReady';
import { useShellStore } from '../shellStore';
import { ORDERS_WORLDS } from '../portalMeta';
import FilterBar from '../FilterBar';

// ============================================================
// OrdersFilterBar — the Orders identity in the v3 grammar (S5): the three
// worlds always visible (Order Desk disabled until its package has run, as
// the live switch), the section menu per world with the counts the hidden
// navs publish (Today's badge, the Collection's Exceptions in red), the LIVE
// slot. Every switch runs through the Table's unsaved-edits guard when one is
// published. What the portal publishes rides the same bar (11.10): the Book's
// LINE · YEAR · SCOPE axes, its live pills and history chip, the Desk's New
// order and lanes · filters · search · rows, Focus · Help for every world —
// `bars.orders` (OrdersPortal, shell/useOrdersShellBar) and `bars['orders-desk']`
// (DeskOrdersList) merged over the identity row this file owns. What the
// Table and the Collection portal into the deck stays as row 3 (ordersV3.css):
// nothing the live screens have is dropped — it is re-housed under the bar.
// ============================================================

const SECTION_TONE = { exceptions: 'red' };

export default function OrdersFilterBar({ condensed, chromeProps }) {
  const world = useOrdersUiStore((s) => s.world);
  const ordersView = useOrdersUiStore((s) => s.ordersView);
  const deskView = useOrdersUiStore((s) => s.deskView);
  const collectionView = useOrdersUiStore((s) => s.collection.view);
  const sectionCounts = useShellStore((s) => s.sectionCounts);
  const navGuard = useShellStore((s) => s.navGuard);
  const published = useShellStore((s) => s.bars.orders || null);
  const deskPublished = useShellStore((s) => (world === 'desk' ? s.bars['orders-desk'] || null : null));
  // CLB-115 S1 — the Desk v12 frame's pulse strip leads the status slot on every Desk screen
  const deskPulse = useShellStore((s) => (world === 'desk' ? s.bars['orders-pulse'] || null : null));
  // CLB-064 — a view's own publish: the Book's Overview (TYPE · Supplier ·
  // Material, its result, Cards & display, New analysis) or the Collection
  // (line · season · the Wall's filters, search, group, sort, density)
  const viewPublished = useShellStore((s) => (world === 'book' ? s.bars['orders-view'] || null : world === 'collection' ? s.bars['orders-collection'] || null : null));
  const desk = useDeskReady();
  const guarded = (run) => { if (navGuard) navGuard(run); else run(); };

  const { sections, section, onSection } = useMemo(() => {
    const w = ORDERS_WORLDS.find((x) => x.key === world);
    const counts = (sectionCounts && sectionCounts[world]) || {};
    const deskLit = deskView === 'new' || deskView === 'card' || deskView === 'batch' ? 'orders' : deskView === 'model' ? 'models' : deskView;   // CLB-115 S4d/S4e · the model page sits under Models
    return {
      sections: w ? w.sections.map((s) => ({ key: s.key, label: s.label, count: counts[s.key] != null ? counts[s.key] : undefined, tone: SECTION_TONE[s.key] })) : [],
      section: world === 'book' ? ordersView : world === 'desk' ? deskLit : world === 'collection' ? collectionView : null,
      onSection: (k) => guarded(() => { const st = useOrdersUiStore.getState(); if (world === 'book') st.setOrdersView(k); else if (world === 'desk') st.setDeskView(k); else st.setCollection({ view: k }); }),
    };
  }, [world, ordersView, deskView, collectionView, sectionCounts, navGuard]);

  const base = {
    portal: { key: 'orders', name: 'Orders', icon: 'box' },
    worlds: ORDERS_WORLDS.map((w) => ({ key: w.key, label: w.label, icon: w.icon, disabled: w.key === 'desk' && desk.ready === false && !desk.isLoading, title: w.key === 'desk' && desk.ready === false && !desk.isLoading ? 'The Order Desk opens once its SQL package has run' : w.label })),
    world,
    onWorld: (next) => guarded(() => { const st = useOrdersUiStore.getState(); st.setWorld(next); try { localStorage.setItem('clb.orders.world', next); } catch (e) {} }),
    sections, section, onSection,
    status: [world === 'collection' ? { dot: 'live', text: 'Live', title: 'Sun and Optic refresh from the Orders Book' } : { dot: 'live', text: 'Live', title: world === 'book' ? 'The Book follows the database — the rail says when the live rows failed' : 'Live' }],
    fields: [],
  };
  // The identity row (portal · worlds · sections · guard) is this file's; the
  // publishers own the rest. A published `status` replaces the plain LIVE pill.
  // the Navigator lists all three worlds: a pick in another world switches the world, then opens that section
  const onPickIn = (w, k) => { if (w === 'desk' && desk.ready === false && !desk.isLoading) return; guarded(() => {
    const st = useOrdersUiStore.getState();
    st.setWorld(w); try { localStorage.setItem('clb.orders.world', w); } catch (e) {}
    if (w === 'book') st.setOrdersView(k); else if (w === 'desk') st.setDeskView(k); else st.setCollection({ view: k });
  }); };
  const own = { portal: base.portal, worlds: base.worlds, world: base.world, onWorld: base.onWorld, sections, section, onSection, worldCounts: sectionCounts, onPickIn };
  const cfg = { ...base, ...(published || {}), ...(deskPublished || {}), ...(viewPublished || {}), ...own };
  if (!cfg.status) cfg.status = base.status;
  if (deskPulse && deskPulse.status && deskPulse.status.length) cfg.status = [...deskPulse.status, ...(cfg.status === base.status ? [] : cfg.status)];
  // The rail merges by GROUP, never whole: the portal owns SCOPE (the Book's
  // line · years · sub), a view adds its FILTERS and the RESULT it computes.
  const railOf = (p) => (p && p.rail) || null;
  const parts = [railOf(published), railOf(deskPublished), railOf(viewPublished)].filter(Boolean);
  cfg.rail = parts.length ? parts.reduce((acc, r) => ({ ...acc, ...Object.fromEntries(Object.entries(r).filter(([, v]) => v !== undefined)) }), {}) : undefined;
  return <FilterBar cfg={cfg} condensed={condensed} {...chromeProps} />;
}
